/**
 * Staff-ui TeamPage form (create + edit), unified into a single mode-aware
 * component.
 *
 * Aligned with the canonical account-ui `TeamPageForm`
 * (`services/account-ui/src/pages/Profiles/TeamPageForm/TeamPageForm.tsx`) via
 * the shared staff-ui primitives in `@/components/Profiles` (ImageUploadField,
 * RichTextField, Repeater, LanguageTabBar, useLanguageVersions, StatusPill,
 * SaveActions, FormValidationSummary, useFormValidation) plus the ported
 * lawyer `Typeahead`.
 *
 * Phase-4 parity changes vs the old staff stub:
 *  - Header image + Team photo inline uploads.
 *  - Award logo inline upload; award shape `{title, logoHash, logoFilename, shortDescription}`.
 *  - Whitepaper Title (required) + inline document upload + RichTextField short description.
 *  - Testimonial body + deal content as RichTextField (preset "basic").
 *  - Team-member lawyer Typeahead (real search) replacing the raw ID input;
 *    member `role` dropped (no backend column).
 *  - Status: read-only StatusPill + admin-gated SaveActions (no editable Select).
 *  - Page type: route-derived / read-only (no editable Select).
 *  - Firm shown by NAME (FirmChip) in edit mode, not the raw UUID.
 *  - Multi-language LanguageTabBar; zod + FormValidationSummary + inline errors.
 *
 * Staff-only additions (the CompanyPicker in create mode + the firm display in
 * edit mode) are retained because staff author across any firm, unlike the
 * account portal which derives the firm from the session.
 */
import {
  Button,
  EditorialCategorySelect,
  type EditorialCategoryResult,
  Input,
} from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  type ContentStatus,
  extractZodIssues,
  type ImageRef,
  type LawyerProfile,
  lawyerProfileKeys,
  lawyerProfilesService,
  type RegionAssignment,
  type TeamAward,
  type TeamDeal,
  type TeamMember,
  type TeamPage,
  teamPageKeys,
  type TeamTestimonial,
  type TeamWhitepaper,
} from "@/api/profiles";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PageHeader } from "@/components/PageHeader";
import {
  FormValidationSummary,
  ImageUploadField,
  LanguageTabBar,
  LANGUAGE_LABELS,
  Repeater,
  SaveActions,
  StatusPill,
  useFormValidation,
  useLanguageHotkeys,
  useLanguageVersions,
} from "@/components/Profiles";
import { editorialCategories as editorialCategoriesApi } from "@/api/editorial-categories.service";
import { CompanyPicker, FirmChip } from "@/components/Profiles/ListFilters";
import { RegionsPicker } from "@/components/Profiles/RegionPicker";
import { RichTextField } from "@/components/Profiles/RichTextField";
// Typeahead is imported by direct path: the shared barrel
// (components/Profiles/index.ts) is owned by a sibling agent, so we do NOT edit
// it. See task ownership notes.
import { Typeahead } from "@/components/Profiles/Typeahead/Typeahead";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import styles from "../ProfilesForm.module.css";
import { AuditPanel } from "../Audit/AuditPanel";
import teamStyles from "./StaffTeamPageForm.module.css";
import {
  createTeamPage,
  fetchTeamPage,
  removeTeamPage,
  updateTeamPage,
} from "./teamPagesMutations";

const ADD_LANGUAGE_CODES = ["en", "fr", "de"];
const RICH_TEXT_MAX = 50_000;

/**
 * The profiles-api accepts `"team" | "clerks"` for `pageType`. The shared
 * staff-ui `PageType` alias is the stale legacy enum
 * (`meet_the_team`/`meet_the_clerks`) and is unused by the API, so we use a
 * narrow local type here to match the real wire contract.
 */
type TeamPageType = "team" | "clerks";

const PAGE_TYPE_LABEL: Record<string, string> = {
  team: "Teams",
  clerks: "Clerks",
};

// ── Per-language payload (only the active language is persisted today). ──
type VersionPayload = {
  teamName: string;
  overview: string;
  status: ContentStatus;
  teamEmail: string;
  teamPhone: string;
  teamWebsite: string;
  headerImage: ImageRef | null;
  teamPhoto: ImageRef | null;
};

function emptyVersion(): VersionPayload {
  return {
    teamName: "",
    overview: "",
    status: "draft",
    teamEmail: "",
    teamPhone: "",
    teamWebsite: "",
    headerImage: null,
    teamPhoto: null,
  };
}

function refFrom(hash: unknown, filename: unknown): ImageRef | null {
  return typeof hash === "string" && typeof filename === "string" ? { hash, filename } : null;
}

function fromApi(p: TeamPage & Record<string, unknown>): VersionPayload {
  return {
    teamName: p.teamName ?? "",
    overview: (p.overview as string) ?? "",
    status: p.status === "draft" || p.status === "published" ? p.status : "draft",
    teamEmail: (p.teamEmail as string) ?? "",
    teamPhone: (p.teamPhone as string) ?? "",
    teamWebsite: (p.teamWebsite as string) ?? "",
    headerImage: refFrom(p.headerImageHash, p.headerImageFilename),
    teamPhoto: refFrom(p.teamPhotoHash, p.teamPhotoFilename),
  };
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

// ── Validation (mirrors account-ui TeamPageDraftSchema / TeamWhitepaperDraftSchema) ──
const TeamWhitepaperDraftSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  documentHash: z.string().nullable().optional(),
  documentFilename: z.string().nullable().optional(),
  shortDescription: z.string().max(RICH_TEXT_MAX, "Short description is too long").optional(),
  sortOrder: z.number(),
});

const TeamPageDraftSchema = z.object({
  teamName: z.string().trim().min(1, "Team name is required"),
  teamWhitepapers: z.array(TeamWhitepaperDraftSchema).optional(),
});

type TeamPageFieldKey = "teamName";

const TEAM_PAGE_FIELD_KEYS = ["teamName"] as const satisfies ReadonlyArray<TeamPageFieldKey>;

const TEAM_PAGE_FIELD_LABELS: Record<TeamPageFieldKey, string> = {
  teamName: "Team name",
};

const TEAM_PAGE_ARRAY_KEYS = ["teamWhitepapers"] as const;

const TEAM_PAGE_ARRAY_FIELD_LABELS = {
  teamWhitepapers: {
    section: "Whitepapers",
    rowNoun: "whitepaper",
    subFields: {
      title: "Title",
      shortDescription: "Short description",
    },
  },
};

export type StaffTeamPageFormProps =
  | { mode: "edit"; id: string; initialCompanyId?: never }
  | { mode: "create"; id?: never; initialCompanyId?: string };

export function StaffTeamPageForm(props: StaffTeamPageFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = props.mode === "edit";
  const { hasPermission } = usePermissions();
  const canPublish = hasPermission("profiles.publish");

  const detailQuery = useQuery({
    queryKey: isEdit ? teamPageKeys.detail(props.id) : ["__noop_team_page_detail__"],
    queryFn: () => fetchTeamPage(props.id as string),
    enabled: isEdit,
  });

  // ── Multi-language working state ──
  const initialVersions = useMemo(() => {
    if (!isEdit || !detailQuery.data) return [];
    const code = (detailQuery.data.languageCode as string | undefined) ?? "en";
    return [{ ...fromApi(detailQuery.data), languageCode: code }];
  }, [isEdit, detailQuery.data]);

  const {
    versions,
    activeCode,
    setActiveCode,
    activeVersion,
    patchActive,
    addLanguage,
    removeLanguage,
    markSaved,
    tabOptions,
    dirtyCodes,
  } = useLanguageVersions<VersionPayload>({
    initial: initialVersions,
    createEmpty: () => emptyVersion(),
    defaultActiveCode: "en",
  });

  useLanguageHotkeys({ codes: tabOptions.map(o => o.code), activeCode, setActiveCode });

  // Seed an English version in create-mode on first render.
  const seededRef = useRef(false);
  useEffect(() => {
    if (isEdit || seededRef.current) return;
    if (versions.length === 0) {
      seededRef.current = true;
      addLanguage("en");
    }
  }, [isEdit, versions.length, addLanguage]);

  // ── Shared (non-per-language) child collections + ownership. ──
  // Page type is route-derived/locked to match the canonical form: in create
  // mode it comes from the navigation prop default; in edit mode from the
  // record. It is never an editable control.
  const [companyId, setCompanyId] = useState<string | null>(
    props.mode === "create" ? (props.initialCompanyId ?? null) : null,
  );

  // Shared lawyer typeahead search, server-side (mirrors ArticleForm's
  // interview-contact typeahead). The list endpoint pages at 20 by default, so
  // filtering a single page client-side hid every lawyer beyond the first page.
  // Instead we send the typed term to the server on each (debounced) keystroke;
  // the single query result is shared by every member Typeahead on the form and
  // scoped to the firm once one is known.
  const [lawyerSearch, setLawyerSearch] = useState("");
  const [debouncedLawyerSearch, setDebouncedLawyerSearch] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedLawyerSearch(lawyerSearch.trim());
    }, 200);
    return () => window.clearTimeout(handle);
  }, [lawyerSearch]);

  const lawyerProfilesQuery = useQuery({
    queryKey: lawyerProfileKeys.list({
      companyId: companyId ?? undefined,
      q: debouncedLawyerSearch || undefined,
    }),
    queryFn: () =>
      lawyerProfilesService.list({
        ...(companyId ? { companyId } : {}),
        ...(debouncedLawyerSearch ? { q: debouncedLawyerSearch } : {}),
        limit: 50,
      }),
  });
  const lawyerOptions: LawyerProfile[] = useMemo(
    () => lawyerProfilesQuery.data?.data ?? [],
    [lawyerProfilesQuery.data],
  );

  // Accumulate every lawyer we've seen across searches. A single shared search
  // term drives all member Typeaheads, so narrowing the query drops
  // already-selected lawyers from the current page — this cache keeps their
  // labels resolvable so other rows don't blank out while one row is searched.
  const lawyerCacheRef = useRef<Map<string, LawyerProfile>>(new Map());
  useEffect(() => {
    for (const lawyer of lawyerOptions) {
      lawyerCacheRef.current.set(lawyer.id, lawyer);
    }
  }, [lawyerOptions]);

  const [pageType, setPageType] = useState<TeamPageType>("team");
  const [regions, setRegions] = useState<RegionAssignment[]>([]);
  const [editorialCategoryIds, setEditorialCategoryIds] = useState<number[]>([]);
  const [testimonials, setTestimonials] = useState<TeamTestimonial[]>([]);
  const [awards, setAwards] = useState<TeamAward[]>([]);
  const [deals, setDeals] = useState<TeamDeal[]>([]);
  const [whitepapers, setWhitepapers] = useState<TeamWhitepaper[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isClerks = pageType === "clerks";

  // ── Editorial category picker options (shared EditorialCategorySelect) ──
  // The full global practice-area taxonomy (Region › optional Practice Group ›
  // Practice Area) is loaded once and handed wholesale to the picker, which does
  // its own search / tree / chip handling.
  const allCategoriesQuery = useQuery({
    queryKey: ["editorial-categories", "all"],
    queryFn: () => editorialCategoriesApi.fetchAll(),
  });

  const allCategories = useMemo<EditorialCategoryResult[]>(
    () => allCategoriesQuery.data ?? [],
    [allCategoriesQuery.data],
  );

  // Hydrate shared child arrays + ownership once the record arrives.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const existing = detailQuery.data;
    if (!isEdit || !existing) return;
    const extras = existing as TeamPage & Record<string, unknown>;
    setCompanyId(existing.companyId);
    // `pageType` on the shared type is the stale legacy enum; the wire value is
    // actually "team" | "clerks", so read it as a plain string.
    const rawPageType = (existing as Record<string, unknown>).pageType;
    setPageType(rawPageType === "clerks" ? "clerks" : "team");
    // Country-only (§4.2): ignore any legacy `kind:"region"` rows on hydration.
    setRegions((existing.regions ?? []).filter(r => r.kind === "country"));
    if (Array.isArray(extras.editorialCategoryIds)) {
      setEditorialCategoryIds(extras.editorialCategoryIds as number[]);
    } else if (Array.isArray(extras.editorialCategories)) {
      // profiles-api returns `editorialCategories: [{ id, editorialCategoryId }]`;
      // tolerate a bare number[] too for defensiveness.
      const raw = extras.editorialCategories as Array<number | { editorialCategoryId: number }>;
      setEditorialCategoryIds(
        raw.map(entry => (typeof entry === "number" ? entry : entry.editorialCategoryId)),
      );
    }
    setTestimonials(asArray<TeamTestimonial>(extras.testimonials));
    setAwards(asArray<TeamAward>(extras.awards));
    setDeals(asArray<TeamDeal>(extras.deals));
    setWhitepapers(asArray<TeamWhitepaper>(extras.whitepapers));
    setMembers(asArray<TeamMember>(extras.members));
  }, [isEdit, detailQuery.data]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const {
    errors: fieldErrors,
    arrayErrors,
    summary,
    orphanMessages,
    validate,
    clearError,
    clearArrayError,
    setServerIssues,
  } = useFormValidation({
    schema: TeamPageDraftSchema,
    labels: TEAM_PAGE_FIELD_LABELS,
    knownKeys: TEAM_PAGE_FIELD_KEYS,
    arrayKeys: TEAM_PAGE_ARRAY_KEYS,
    arrayFieldLabels: TEAM_PAGE_ARRAY_FIELD_LABELS,
  });

  const teamNameRef = useRef<HTMLInputElement | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (targetStatus: ContentStatus): Promise<TeamPage> => {
      if (!activeVersion) throw new Error("No active language version to save");
      const basePayload: Record<string, unknown> = {
        pageType,
        teamName: activeVersion.teamName,
        overview: activeVersion.overview || undefined,
        status: targetStatus,
        headerImageHash: activeVersion.headerImage?.hash ?? null,
        headerImageFilename: activeVersion.headerImage?.filename ?? null,
        teamPhotoHash: activeVersion.teamPhoto?.hash ?? null,
        teamPhotoFilename: activeVersion.teamPhoto?.filename ?? null,
        teamEmail: activeVersion.teamEmail || undefined,
        teamPhone: activeVersion.teamPhone || undefined,
        teamWebsite: activeVersion.teamWebsite || undefined,
        languageCode: activeCode,
        regions,
        // profiles-api accepts the practice-area selection as `editorialCategories`.
        editorialCategories: editorialCategoryIds,
        testimonials: testimonials.map((t, idx) => ({ ...t, sortOrder: idx })),
        awards: awards.map((a, idx) => ({ ...a, sortOrder: idx })),
        deals: deals.map((d, idx) => ({ ...d, sortOrder: idx })),
        whitepapers: whitepapers.map((w, idx) => ({ ...w, sortOrder: idx })),
        members: members.map((m, idx) => ({ ...m, sortOrder: idx })),
      };
      if (isEdit) {
        return updateTeamPage(props.id as string, basePayload);
      }
      if (!companyId) {
        throw new Error("Company is required to create a team page");
      }
      return createTeamPage({ ...basePayload, companyId });
    },
    onSuccess: async (saved, targetStatus) => {
      if (activeVersion) {
        markSaved([{ ...activeVersion, status: targetStatus }]);
      }
      await queryClient.invalidateQueries({ queryKey: teamPageKeys.all });
      navigate(ROUTES.profiles.teamPages);
    },
    onError: (err: Error) => {
      const issues = extractZodIssues(err);
      if (issues && issues.length > 0) {
        setServerIssues(issues);
        setFormError(null);
        return;
      }
      setFormError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => removeTeamPage(props.id as string),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: teamPageKeys.all });
      navigate(ROUTES.profiles.teamPages);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  if (isEdit && detailQuery.isPending) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Team page" />
        <p role="status" className={styles.muted}>
          Loading…
        </p>
      </div>
    );
  }
  if (isEdit && detailQuery.isError) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Team page" />
        <p role="alert" className={styles.errorBanner}>
          {detailQuery.error instanceof Error
            ? detailQuery.error.message
            : "Failed to load team page"}
        </p>
      </div>
    );
  }
  if (!activeVersion) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title={isEdit ? "Team page" : "New team page"} />
        <p role="status" className={styles.muted}>
          Loading…
        </p>
      </div>
    );
  }

  const version = activeVersion;

  const handleSave = (targetStatus: ContentStatus) => {
    setFormError(null);
    setRegionsError(null);

    if (!isEdit && !companyId) {
      setFormError("Company is required");
      return;
    }

    const parsed = validate({ teamName: version.teamName, teamWhitepapers: whitepapers });
    if (parsed === null) {
      if (!version.teamName.trim()) {
        setTimeout(() => teamNameRef.current?.focus(), 0);
      }
      return;
    }

    if (regions.length === 0) {
      setRegionsError("At least one region is required");
      return;
    }

    saveMutation.mutate(targetStatus);
  };

  const currentStatus: ContentStatus =
    version.status === "draft" || version.status === "published" ? version.status : "draft";

  const availableLanguageCodes = ADD_LANGUAGE_CODES.filter(
    code => !versions.some(v => v.languageCode === code),
  );
  const canRemoveLanguage = versions.length > 1;
  const pageTypeLabel = PAGE_TYPE_LABEL[pageType] ?? pageType;

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={isEdit ? `Edit team page: ${version.teamName || "untitled"}` : "New team page"}
        meta={isEdit && companyId ? <FirmChip companyId={companyId} /> : undefined}
      >
        <div className={styles.headerActions}>
          <Button variant="outlined" onClick={() => navigate(ROUTES.profiles.teamPages)}>
            Cancel
          </Button>
          <SaveActions
            layout="inline"
            currentStatus={currentStatus}
            canPublish={canPublish}
            isSaving={saveMutation.isPending || deleteMutation.isPending}
            onSave={handleSave}
            onDelete={isEdit ? () => setDeleteOpen(true) : undefined}
          />
        </div>
      </PageHeader>

      <FormValidationSummary
        entries={summary}
        onEntryActivate={key => {
          if (key === "teamName") {
            setTimeout(() => teamNameRef.current?.focus(), 0);
          }
        }}
      />

      {(formError || orphanMessages.length > 0) && (
        <div role="alert" className={styles.errorBanner}>
          {formError ?? orphanMessages.join("\n")}
        </div>
      )}

      <LanguageTabBar
        languages={tabOptions}
        activeCode={activeCode}
        onChange={setActiveCode}
        availableLanguages={availableLanguageCodes.map(code => ({
          code,
          label: LANGUAGE_LABELS[code] ?? code.toUpperCase(),
        }))}
        onAdd={addLanguage}
        onRemove={canRemoveLanguage ? removeLanguage : undefined}
        addLabel="Add language"
      />

      {dirtyCodes.length > 1 && (
        <p className={styles.muted}>
          Multi-language save is not yet supported — only the active language (
          {LANGUAGE_LABELS[activeCode] ?? activeCode}) will be persisted.
        </p>
      )}

      <div className={styles.layout}>
        <div className={styles.main}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Basics</h2>
            <Input
              ref={teamNameRef}
              label="Team name"
              fullWidth
              required
              aria-required="true"
              error={fieldErrors.teamName}
              value={version.teamName}
              onChange={e => {
                patchActive({ teamName: e.target.value });
                clearError("teamName");
              }}
            />
            <RichTextField
              label="Overview"
              preset="basic"
              mediaEnabled={false}
              value={version.overview}
              onChange={html => patchActive({ overview: html })}
            />
            <ImageUploadField
              label="Header image"
              value={version.headerImage}
              onChange={headerImage => patchActive({ headerImage })}
              slug={version.teamName || undefined}
            />
            <ImageUploadField
              label="Team photo"
              value={version.teamPhoto}
              onChange={teamPhoto => patchActive({ teamPhoto })}
              slug={version.teamName || undefined}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Contact</h2>
            <div className={teamStyles.fieldRow3}>
              <Input
                type="email"
                label="Team email"
                fullWidth
                value={version.teamEmail}
                onChange={e => patchActive({ teamEmail: e.target.value })}
              />
              <Input
                label="Team phone"
                fullWidth
                value={version.teamPhone}
                onChange={e => patchActive({ teamPhone: e.target.value })}
              />
              <Input
                type="url"
                label="Team website"
                fullWidth
                value={version.teamWebsite}
                onChange={e => patchActive({ teamWebsite: e.target.value })}
              />
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Team members</h2>
            <Repeater<TeamMember>
              items={members}
              onChange={setMembers}
              createEmpty={() => ({ lawyerProfileId: "", sortOrder: members.length })}
              addLabel="Add member"
              emptyState="No members added yet."
              renderRow={(item, _index, update) => {
                const selected =
                  lawyerOptions.find(l => l.id === item.lawyerProfileId) ??
                  lawyerCacheRef.current.get(item.lawyerProfileId) ??
                  null;
                return (
                  <div className={teamStyles.repeaterRow}>
                    <Typeahead<LawyerProfile>
                      label="Lawyer"
                      value={selected}
                      onChange={next => update({ lawyerProfileId: next ? next.id : "" })}
                      onInputChange={setLawyerSearch}
                      options={lawyerOptions}
                      getLabel={l => `${l.firstName} ${l.lastName}`.trim()}
                      getSublabel={l => l.position ?? l.email ?? ""}
                      getKey={l => l.id}
                      placeholder="Search for a lawyer"
                      loading={
                        lawyerSearch.trim() !== debouncedLawyerSearch ||
                        lawyerProfilesQuery.isFetching
                      }
                    />
                  </div>
                );
              }}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Testimonials</h2>
            <Repeater<TeamTestimonial>
              items={testimonials}
              onChange={setTestimonials}
              createEmpty={() => ({
                clientName: "",
                clientCompany: "",
                testimonial: "",
                sortOrder: testimonials.length,
              })}
              addLabel="Add testimonial"
              emptyState="No testimonials added yet."
              renderRow={(item, _index, update) => (
                <div className={teamStyles.repeaterRow}>
                  <div className={teamStyles.fieldRow}>
                    <Input
                      label="Client name"
                      fullWidth
                      value={item.clientName}
                      onChange={e => update({ clientName: e.target.value })}
                    />
                    <Input
                      label="Client company"
                      fullWidth
                      value={item.clientCompany ?? ""}
                      onChange={e => update({ clientCompany: e.target.value })}
                    />
                  </div>
                  <RichTextField
                    label="Testimonial"
                    preset="basic"
                    mediaEnabled={isClerks}
                    value={item.testimonial}
                    onChange={html => update({ testimonial: html })}
                  />
                </div>
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Awards</h2>
            <Repeater<TeamAward>
              items={awards}
              onChange={setAwards}
              createEmpty={() => ({
                title: "",
                logoHash: null,
                logoFilename: null,
                shortDescription: "",
                sortOrder: awards.length,
              })}
              addLabel="Add award"
              emptyState="No awards added yet."
              renderRow={(item, _index, update) => (
                <div className={teamStyles.repeaterRow}>
                  <Input
                    label="Award title"
                    fullWidth
                    value={item.title}
                    onChange={e => update({ title: e.target.value })}
                  />
                  <ImageUploadField
                    label="Logo"
                    value={
                      item.logoHash && item.logoFilename
                        ? { hash: item.logoHash, filename: item.logoFilename }
                        : null
                    }
                    onChange={ref =>
                      update({
                        logoHash: ref?.hash ?? null,
                        logoFilename: ref?.filename ?? null,
                      })
                    }
                  />
                  <Input
                    label="Short description"
                    fullWidth
                    value={item.shortDescription}
                    onChange={e => update({ shortDescription: e.target.value })}
                  />
                </div>
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Deals</h2>
            <Repeater<TeamDeal>
              items={deals}
              onChange={setDeals}
              createEmpty={() => ({ content: "", sortOrder: deals.length })}
              addLabel="Add deal"
              emptyState="No deals added yet."
              renderRow={(item, _index, update) => (
                <div className={teamStyles.repeaterRow}>
                  <RichTextField
                    label="Deal"
                    preset="basic"
                    mediaEnabled={false}
                    value={item.content}
                    onChange={html => update({ content: html })}
                  />
                </div>
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Whitepapers</h2>
            <Repeater<TeamWhitepaper>
              items={whitepapers}
              onChange={setWhitepapers}
              rowErrors={arrayErrors.teamWhitepapers}
              createEmpty={() => ({
                title: "",
                documentHash: null,
                documentFilename: null,
                shortDescription: "",
                sortOrder: whitepapers.length,
              })}
              addLabel="Add whitepaper"
              emptyState="No whitepapers added yet."
              renderRow={(item, index, update, errs) => (
                <div className={teamStyles.repeaterRow}>
                  <Input
                    label="Title"
                    fullWidth
                    required
                    aria-required="true"
                    error={errs?.title}
                    value={item.title}
                    onChange={e => {
                      update({ title: e.target.value });
                      clearArrayError("teamWhitepapers", index, "title");
                    }}
                  />
                  <ImageUploadField
                    label="Document"
                    accept="application/pdf,image/png,image/jpeg,image/gif,image/webp"
                    value={
                      item.documentHash && item.documentFilename
                        ? { hash: item.documentHash, filename: item.documentFilename }
                        : null
                    }
                    onChange={ref =>
                      update({
                        documentHash: ref?.hash ?? null,
                        documentFilename: ref?.filename ?? null,
                      })
                    }
                  />
                  <RichTextField
                    label="Short description"
                    preset="full"
                    mediaEnabled={isClerks}
                    htmlTab
                    error={errs?.shortDescription}
                    value={item.shortDescription ?? ""}
                    onChange={html => {
                      update({ shortDescription: html });
                      clearArrayError("teamWhitepapers", index, "shortDescription");
                    }}
                  />
                </div>
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Practice areas</h2>
            <EditorialCategorySelect
              label="Practice areas"
              value={editorialCategoryIds}
              onChange={setEditorialCategoryIds}
              options={allCategories}
            />
          </section>
        </div>

        <aside className={styles.sidebar}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Status</h2>
            <StatusPill status={currentStatus} />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Page type</h2>
            {/* Route-derived / locked to match the canonical account-ui form. */}
            <p className={styles.muted}>{pageTypeLabel}</p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Countries</h2>
            <RegionsPicker
              label="Countries (at least one required)"
              value={regions}
              onChange={next => {
                setRegions(next);
                if (next.length > 0) setRegionsError(null);
              }}
              error={regionsError}
            />
          </section>

          {!isEdit && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Company</h2>
              <CompanyPicker value={companyId} onChange={setCompanyId} />
              <p className={styles.labelHint}>
                Required: select the firm this team page belongs to.
              </p>
            </section>
          )}

          {props.mode === "edit" && <AuditPanel entityType="team_page" entityId={props.id} />}
        </aside>
      </div>

      {deleteOpen && isEdit && (
        <ConfirmationModal
          title={`Delete team page "${version.teamName || "untitled"}"?`}
          message="This will permanently delete the team page."
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setDeleteOpen(false)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync();
          }}
        />
      )}
    </div>
  );
}
